// GCS resumable upload (https://cloud.google.com/storage/docs/performing-resumable-uploads):
//
//   POST signed URL, x-goog-resumable: start    → 201, Location: session URI
//   PUT session URI, file bytes                  → 200/201 when the object is complete
//   PUT session URI, Content-Range: bytes */size → 308, Range: bytes=0-N after a dropped connection
//   PUT session URI, bytes N+1..end              → resumes instead of sending the whole file again
//
// XHR, not fetch: only XHR reports upload progress.

export interface StorageResponse {
  // 0 when there was no response: network error, timeout, CORS failure.
  status: number;
  header: (name: string) => string | null;
}

export interface StorageRequest {
  method: "POST" | "PUT";
  url: string;
  headers: Record<string, string>;
  body?: Blob;
  onProgress?: (loaded: number) => void;
}

export type SendStorageRequest = (request: StorageRequest) => Promise<StorageResponse>;

export class StorageUploadError extends Error {
  constructor(public status: number) {
    super(`Storage upload failed with status ${status}`);
  }
}

export const MAX_FAILURES = 6;

const isComplete = (status: number) => status === 200 || status === 201;

const isRetryable = (status: number) =>
  status === 0 || status === 308 || status === 408 || status === 429 || status >= 500;

// "bytes=0-524287" → 524288: the first byte GCS does not have yet.
export const nextOffset = (range: string | null): number => {
  const match = range?.match(/bytes=0-(\d+)/);
  return match ? Number(match[1]) + 1 : 0;
};

const sendWithXhr: SendStorageRequest = ({ method, url, headers, body, onProgress }) =>
  new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    Object.entries(headers).forEach(([name, value]) => xhr.setRequestHeader(name, value));
    if (onProgress) {
      xhr.upload.onprogress = (event) => onProgress(event.loaded);
    }
    const done = () => resolve({ status: xhr.status, header: (name) => xhr.getResponseHeader(name) });
    xhr.onload = done;
    xhr.onerror = done;
    xhr.ontimeout = done;
    xhr.onabort = done;
    xhr.send(body ?? null);
  });

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Uploads [file] through a signed resumable-start URL, resuming after dropped connections. */
export async function resumableUpload(
  signedUrl: string,
  file: Blob,
  onProgress: (loaded: number) => void,
  send: SendStorageRequest = sendWithXhr,
  wait: (ms: number) => Promise<unknown> = sleep,
): Promise<void> {
  let failures = 0;
  const backOff = async (status: number) => {
    failures += 1;
    if (!isRetryable(status) || failures >= MAX_FAILURES) {
      throw new StorageUploadError(status);
    }
    await wait(1000 * 2 ** (failures - 1));
  };

  let sessionUrl: string | null = null;
  while (!sessionUrl) {
    const started = await send({ method: "POST", url: signedUrl, headers: { "x-goog-resumable": "start" } });
    sessionUrl = started.status === 201 ? started.header("Location") : null;
    if (!sessionUrl) {
      await backOff(started.status);
    }
  }
  const session = sessionUrl;

  // Asks GCS how many bytes it kept; null when the object is already complete.
  const persistedOffset = async (): Promise<number | null> => {
    for (;;) {
      const status = await send({ method: "PUT", url: session, headers: { "Content-Range": `bytes */${file.size}` } });
      if (isComplete(status.status)) {
        return null;
      }
      if (status.status === 308) {
        return nextOffset(status.header("Range"));
      }
      await backOff(status.status);
    }
  };

  let offset: number | null = 0;
  while (offset !== null) {
    const from = offset;
    const put = await send({
      method: "PUT",
      url: session,
      headers: from > 0 ? { "Content-Range": `bytes ${from}-${file.size - 1}/${file.size}` } : {},
      body: file.slice(from),
      onProgress: (loaded) => onProgress(from + loaded),
    });
    if (isComplete(put.status)) {
      break;
    }
    await backOff(put.status);
    offset = await persistedOffset();
  }
  onProgress(file.size);
}
